"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const HOVER_OPEN_DELAY_MS = 100;
const PASSIVE_CLOSE_DELAY_MS = 250;
const FOCUSABLE_ELEMENTS_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
].join(", ");

type OpenMode = "hover" | "focus" | "activate";

interface ArticleHelpPopoverProps {
    triggerLabel: string;
    contentLabel: string;
    explanation: string;
    href: string;
    linkLabel: string;
    icon: ReactNode;
    triggerClassName?: string;
}

export function ArticleHelpPopover({
    triggerLabel,
    contentLabel,
    explanation,
    href,
    linkLabel,
    icon,
    triggerClassName,
}: ArticleHelpPopoverProps) {
    const [open, setOpen] = useState(false);
    const linkLabelId = useId();
    const newTabNoticeId = useId();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const articleLinkRef = useRef<HTMLAnchorElement>(null);
    const openMode = useRef<OpenMode>("hover");
    const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const restoreFocusOnClose = useRef(false);
    const suppressFocusOpen = useRef(false);

    function clearTimers() {
        if (openTimer.current !== null) {
            clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        if (closeTimer.current !== null) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }

    function scheduleHoverOpen() {
        clearTimers();
        if (open) return;
        openTimer.current = setTimeout(() => {
            openMode.current = "hover";
            setOpen(true);
        }, HOVER_OPEN_DELAY_MS);
    }

    function openFromFocus() {
        clearTimers();
        if (suppressFocusOpen.current) {
            suppressFocusOpen.current = false;
            return;
        }
        if (!open || openMode.current !== "activate") {
            openMode.current = "focus";
        }
        setOpen(true);
    }

    function schedulePassiveClose() {
        clearTimers();
        if (openMode.current === "activate") return;
        if (triggerRef.current === document.activeElement) return;
        closeTimer.current = setTimeout(
            () => setOpen(false),
            PASSIVE_CLOSE_DELAY_MS,
        );
    }

    function focusArticleLink() {
        articleLinkRef.current?.focus();
    }

    function nextFocusableAfterTrigger() {
        const trigger = triggerRef.current;
        if (!trigger) return null;

        const candidates = Array.from(
            document.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR),
        ).filter(
            (element) =>
                !element.hasAttribute("data-radix-focus-guard") &&
                !element.closest('[data-slot="popover-content"]') &&
                !element.closest("[hidden]") &&
                element.getAttribute("aria-hidden") !== "true",
        );
        const triggerIndex = candidates.indexOf(trigger);

        return triggerIndex >= 0 ? (candidates[triggerIndex + 1] ?? null) : null;
    }

    function closeAndFocus(target: HTMLElement | null) {
        clearTimers();
        restoreFocusOnClose.current = false;
        if (target === triggerRef.current) suppressFocusOpen.current = true;
        setOpen(false);
        setTimeout(() => {
            if (target?.isConnected) target.focus();
        }, 0);
    }

    useEffect(
        () => () => {
            if (openTimer.current !== null) clearTimeout(openTimer.current);
            if (closeTimer.current !== null) clearTimeout(closeTimer.current);
        },
        [],
    );

    return (
        <Popover
            modal={false}
            open={open}
            onOpenChange={(nextOpen) => {
                clearTimers();
                setOpen(nextOpen);
            }}
        >
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    aria-label={triggerLabel}
                    className={cn(
                        "inline-flex size-6 shrink-0 cursor-help items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        triggerClassName,
                    )}
                    onPointerEnter={(event) => {
                        if (event.pointerType === "mouse") scheduleHoverOpen();
                    }}
                    onPointerLeave={(event) => {
                        if (event.pointerType === "mouse") schedulePassiveClose();
                    }}
                    onFocus={openFromFocus}
                    onBlur={schedulePassiveClose}
                    onClick={(event) => {
                        event.preventDefault();
                        clearTimers();
                        if (open && openMode.current === "activate") {
                            setOpen(false);
                            return;
                        }
                        openMode.current = "activate";
                        setOpen(true);
                        setTimeout(focusArticleLink, 0);
                    }}
                    onKeyDown={(event) => {
                        if (event.key !== "Tab" || event.shiftKey || !open) return;
                        event.preventDefault();
                        clearTimers();
                        openMode.current = "activate";
                        focusArticleLink();
                    }}
                >
                    {icon}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="top"
                sideOffset={6}
                collisionPadding={12}
                aria-label={contentLabel}
                className="w-72 max-w-[calc(100vw-1.5rem)] space-y-2 px-3 py-2.5"
                onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") clearTimers();
                }}
                onPointerLeave={(event) => {
                    if (event.pointerType === "mouse") schedulePassiveClose();
                }}
                onFocusCapture={clearTimers}
                onOpenAutoFocus={(event) => event.preventDefault()}
                onEscapeKeyDown={() => {
                    restoreFocusOnClose.current = true;
                }}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    if (restoreFocusOnClose.current) {
                        restoreFocusOnClose.current = false;
                        suppressFocusOpen.current = true;
                        triggerRef.current?.focus();
                    }
                }}
            >
                <p className="text-sm leading-relaxed">{explanation}</p>
                <Link
                    ref={articleLinkRef}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-labelledby={`${linkLabelId} ${newTabNoticeId}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    onKeyDown={(event) => {
                        if (event.key !== "Tab") return;
                        event.preventDefault();
                        event.stopPropagation();
                        closeAndFocus(
                            event.shiftKey
                                ? triggerRef.current
                                : nextFocusableAfterTrigger(),
                        );
                    }}
                >
                    <span id={linkLabelId}>{linkLabel}</span>
                    <span className="sr-only" id={newTabNoticeId}>
                        (opens in a new tab)
                    </span>
                    <ArrowUpRight className="size-3.5" aria-hidden="true" />
                </Link>
            </PopoverContent>
        </Popover>
    );
}
